import { FC, useMemo, useState, useContext } from 'react';
import { useSnackbar } from 'notistack';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  Tooltip,
  FormControl,
  Radio,
  FormControlLabel,
  RadioGroup,
  Box,
  Typography,
  useMediaQuery,
  Button,
  IconButton
} from '@mui/material';
import { UnfoldLess, UnfoldMore } from '@mui/icons-material';
import { Table, EditableCell, ImageUpload, ImageModal } from '../../../components';
import { MobileWorkItems } from './MobileWorkItems';
import { createWorkItemImage } from '../../../fetch';
import { IWorkItem, ReactSetState, UserRoles } from '../../../models';
import { UserContext } from '../../../context';

interface IWorkItemsProps {
  workItems: IWorkItem[];
  setWorkItems: ReactSetState<IWorkItem[]>;
  checklistLoading: boolean;
  isDisabled?: boolean;
}

export const WorkItems: FC<IWorkItemsProps> = ({
  setWorkItems,
  workItems,
  checklistLoading,
  isDisabled
}) => {
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [currentImage, setCurrentImage] = useState<string>('');
  const [isSavingImage, setSavingImage] = useState<{
    [id: number]: boolean;
  }>({});
  const [isAccordionsExpanded, setAccordionsExpanded] = useState<boolean>(true);
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useContext(UserContext);

  const hasPermissionToSelectAll = useMemo(() => {
    if (!user?.userRoles) return false;

    const permittedRoles = [
      UserRoles.BLSuperintendent2,
      UserRoles.BLSuperintendent3,
      UserRoles.BLSuperintendent4,
      UserRoles.BLProjectManager,
      UserRoles.BLProjectmanager2,
      UserRoles.BLProjectmanager3
    ];

    return user.userRoles.some(role => permittedRoles.includes(role as UserRoles));
  }, [user]);

  const updateMyData = (workItem: IWorkItem, columnId: string, value: any, error?: boolean) => {
    setWorkItems(old =>
      old.map((row, index) => {
        if (row.workItemId === workItem.workItemId) {
          return {
            ...workItem,
            [columnId]: value
          };
        }
        return row;
      })
    );
  };

  const handleImageUpload = async (file: File, workItem: IWorkItem) => {
    try {
      setSavingImage(prev => ({
        ...prev,
        [workItem.workItemId]: true
      }));

      const formData = new FormData();
      formData.append('File', file);
      await createWorkItemImage(workItem.workItemId, formData);

      enqueueSnackbar(`Image saved!`, {
        variant: 'success'
      });

      // Update the work item to show it has an image and force refresh
      setWorkItems(old =>
        old.map((item: IWorkItem) => {
          if (item.workItemId === workItem.workItemId) {
            return {
              ...item,
              hasImage: true,
              imageTimestamp: Date.now()
            };
          }
          return item;
        })
      );
    } catch (error) {
      enqueueSnackbar(`Error uploading image. Please try again.`, {
        variant: 'error'
      });
    } finally {
      setSavingImage(prev => ({
        ...prev,
        [workItem.workItemId]: false
      }));
    }
  };

  const handleSelectAllMeetsStandards = () => {
    setWorkItems(items =>
      items.map(item => ({
        ...item,
        meetsStandards: true
      }))
    );
    enqueueSnackbar(`All work items updated to 'Meets Standards'.`, {
      variant: 'success'
    });
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Category',
        accessor: 'category',
        sort: false
      },
      {
        Header: 'Description',
        accessor: 'description',
        sort: false,
        overrideWidth: 400
      },
      {
        Header: 'Meets Standards',
        accessor: 'meetsStandards',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }) => {
          return isDisabled ? (
            <>
              {original.meetsStandards !== null ? (original.meetsStandards ? 'Yes' : 'No') : null}
            </>
          ) : (
            <FormControl fullWidth variant="outlined" size="small" required>
              <RadioGroup
                value={
                  original.meetsStandards !== null ? (original.meetsStandards ? 'Yes' : 'No') : null
                }
                onChange={e =>
                  updateMyData(original, 'meetsStandards', e.target.value === 'Yes' ? true : false)
                }
                aria-label="meetsStandards"
                name="meetsStandards"
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          );
        }
      },
      {
        Header: 'Photo',
        accessor: 'photo',
        sort: false,
        Cell: ({ cell: { row } }) => {
          return (
            <Box display="flex">
              {!isDisabled && (
                <Tooltip title="Upload Image" placement="top">
                  <div>
                    <ImageUpload
                      showSource={false}
                      imageKey={row.original.workItemId}
                      disabled={isSavingImage[row.original.workItemId]}
                      showLabel={false}
                      handleFileChange={(_, file) => handleImageUpload(file, row.original)}
                    />
                  </div>
                </Tooltip>
              )}
              {row.original.hasImage && (
                <picture
                  className={classes.pictureImages}
                  onClick={() =>
                    setCurrentImage(
                      `${window.location.origin}/api/purchase-order-checklists/work-items/${row.original.workItemId}/image`
                    )
                  }
                >
                  <img
                    className={classes.colorImage}
                    src={`${window.location.origin}/api/purchase-order-checklists/work-items/${row.original.workItemId}/image`}
                    alt=""
                  />
                </picture>
              )}
            </Box>
          );
        }
      },
      {
        Header: 'Note',
        accessor: 'note',
        sort: false,
        Cell: props =>
          isDisabled ? <>{props.cell.row.original.note}</> : <EditableCell {...props} />
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workItems, isSavingImage, isDisabled]);

  return (
    <Box>
      {!isMobile && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Work Items</Typography>
          {!isDisabled && hasPermissionToSelectAll && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSelectAllMeetsStandards}
              disabled={checklistLoading}
            >
              Select All Meets Standards
            </Button>
          )}
        </Box>
      )}

      {isMobile && (
        <Box>
          {!isDisabled && hasPermissionToSelectAll && (
            <Button
              variant="contained"
              color="secondary"
              size="small"
              fullWidth
              onClick={handleSelectAllMeetsStandards}
              disabled={checklistLoading}
              className={classes.mobileSelectAllButton}
            >
              Select All Meets Standards
            </Button>
          )}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" className={classes.mobileWorkItemsTitle}>
              Work Items
            </Typography>
            <Tooltip title={`${isAccordionsExpanded ? 'Collapse' : 'Expand'} all work items`}>
              <IconButton
                onClick={() => setAccordionsExpanded(!isAccordionsExpanded)}
                size="small"
                className={classes.expandCollapseButton}
              >
                {isAccordionsExpanded ? <UnfoldLess /> : <UnfoldMore />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {!isMobile && (
        <Table
          columns={columns}
          data={workItems}
          isLoading={checklistLoading}
          useTableProps={{
            updateMyData
          }}
          hidePagination
        />
      )}

      {isMobile && (
        <Box>
          {checklistLoading ? (
            <Typography>Loading...</Typography>
          ) : (
            workItems.map((workItem, index) => (
              <MobileWorkItems
                key={`mobile-workitem-${workItem.workItemId}`}
                original={workItem}
                setCurrentImage={setCurrentImage}
                isDisabled={isDisabled}
                handleImageUpload={handleImageUpload}
                isSavingImage={isSavingImage}
                isAllAccordionsExpanded={isAccordionsExpanded}
                updateMyData={updateMyData}
              />
            ))
          )}
        </Box>
      )}

      <ImageModal
        isOpen={!!currentImage}
        onClose={() => setCurrentImage('')}
        source={currentImage}
      />
    </Box>
  );
};

const useStyles = makeStyles<Theme>(theme => ({
  pictureImages: {
    userSelect: 'none',
    cursor: 'pointer',
    '&:hover::before': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      color: theme.palette.common.white,
      content: '"See Larger"',
      fontSize: '0.85rem',
      position: 'absolute',
      textAlign: 'center',
      height: 50,
      width: 50,
      padding: 4
    }
  },
  colorImage: {
    width: 50,
    height: 50
  },
  mobileSelectAllButton: {
    marginBottom: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1.25)
  },
  mobileWorkItemsTitle: {
    marginBottom: theme.spacing(0.25)
  },
  expandCollapseButton: {
    marginLeft: theme.spacing(0.5),
    padding: theme.spacing(0.5)
  }
}));
